import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { ClothingItem, ClothingType } from "../../types";
import { 
  GENDER_CATEGORIES, 
  FASHION_BRANDS, 
  getClothingTypesByGender,
  GenderCategory 
} from "../../data/fashionCategories";
import { 
  FASHION_COLORS, 
  getColorValue, 
  getLightColors 
} from "../../data/fashionColors";

interface ClothingItemFormProps {
  onAddItem: (item: ClothingItem) => void;
  onRemoveItem: (index: number) => void;
  items: ClothingItem[];
}

/**
 * Form for adding and managing clothing items
 */
const ClothingItemForm: React.FC<ClothingItemFormProps> = ({
  onAddItem,
  onRemoveItem,
  items,
}) => {
  // Form state
  const [gender, setGender] = useState<GenderCategory>('Women');
  const [type, setType] = useState<string>('');
  const [brand, setBrand] = useState("");
  const [color, setColor] = useState("");
  const [description, setDescription] = useState("");
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // Get available clothing types based on selected gender
  const availableClothingTypes = useMemo(() => {
    return getClothingTypesByGender(gender);
  }, [gender]);

  // Use curated fashion colors for better UX
  const commonColors = FASHION_COLORS;

  // Reset form
  const resetForm = () => {
    setGender('Women');
    setType('');
    setBrand("");
    setColor("");
    setDescription("");
    setEditIndex(null);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!type.trim()) {
      return; // Don't submit if no type is selected
    }

    const newItem = {
      type: type as ClothingType,
      brand,
      color,
      description,
      gender,
    };

    if (editIndex !== null) {
      // Update existing item
      const updatedItems = [...items];
      updatedItems[editIndex] = newItem;
      onRemoveItem(editIndex);
      onAddItem(newItem);
      setEditIndex(null);
    } else {
      // Add new item
      onAddItem(newItem);
    }

    resetForm();
  };

  // Handle edit item
  const handleEditItem = (index: number) => {
    const item = items[index];
    setGender((item.gender as GenderCategory) || 'Women');
    setType(item.type);
    setBrand(item.brand);
    setColor(item.color);
    setDescription(item.description);
    setEditIndex(index);
  };

  // Handle gender change - reset type when gender changes
  const handleGenderChange = (newGender: GenderCategory) => {
    setGender(newGender);
    setType(''); // Reset type when gender changes
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Clothing Items
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              select
              fullWidth
              label="Gender"
              value={gender}
              onChange={(e) => handleGenderChange(e.target.value as GenderCategory)}
              required
            >
              {GENDER_CATEGORIES.map((genderOption) => (
                <MenuItem key={genderOption} value={genderOption}>
                  {genderOption}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              options={availableClothingTypes}
              value={type}
              onChange={(_, newValue) => setType(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type"
                  required
                  placeholder="Select clothing type"
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              options={FASHION_BRANDS}
              value={brand}
              onChange={(_, newValue) => setBrand(newValue || '')}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  required
                  placeholder="Select or type brand"
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              select
              fullWidth
              label="Color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              required
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                },
              }}
            >
              {commonColors.map((colorOption) => (
                <MenuItem key={colorOption} value={colorOption}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: getColorValue(colorOption),
                        border: colorOption === 'White' || colorOption === 'Cream' ? '1px solid #ccc' : 'none',
                        flexShrink: 0
                      }}
                    />
                    <Typography>{colorOption}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Slim fit jeans"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          {editIndex !== null && (
            <Button
              type="button"
              variant="outlined"
              onClick={resetForm}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
          )}

          <Button
            type="submit"
            variant="contained"
            startIcon={editIndex !== null ? <EditIcon /> : <AddIcon />}
          >
            {editIndex !== null ? "Update Item" : "Add Item"}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom>
        Added Items ({items.length})
      </Typography>

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No clothing items added yet. Add at least one item above.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {items.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card variant="outlined" sx={{ backgroundColor: "#E3F2FD", color: "#1C1C1C" }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {item.type}
                    </Typography>
                    <Chip
                      label={item.color}
                      size="small"
                      sx={{
                        bgcolor: item.color.toLowerCase(),
                        color: ["White", "Yellow", "Beige"].includes(item.color)
                          ? "black"
                          : "white",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Gender: {(item.gender as string) || 'Not specified'}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Brand: {item.brand}
                  </Typography>

                  {item.description && (
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => handleEditItem(index)}
                    aria-label="edit"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={() => onRemoveItem(index)}
                    aria-label="delete"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ClothingItemForm;
